/**
 * Product Store (S1.4)
 *
 * Zustand store for the product list surface — filter state (persisted),
 * pagination echo, and the store-first list cache the mutation hooks update.
 *
 * Filter contract (holistic plan S1.4):
 * - `search` — SKU / Internal Product ID / SCN / names / part numbers
 * - `categoryPaths` — cascader paths into the 5-capable category tree;
 *   filtering always includes every DESCENDANT of a selected node
 * - `readiness` — 產品就緒 two-tier gate filter (all / ready / not_ready)
 * - `scnLink` — SCN has-link filter (all / linked / unlinked)
 * - `isActive` — defaults to TRUE (house list rule; excluded from the badge
 *   count while it sits on the default)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Product } from '@/types/api/product';

export type ProductReadinessFilter = 'all' | 'ready' | 'not_ready';
export type ProductScnLinkFilter = 'all' | 'linked' | 'unlinked';

export interface ProductFilters {
  search: string;
  /** Cascader paths (root → … → selected node); descendants are included. */
  categoryPaths: string[][];
  readiness: ProductReadinessFilter;
  scnLink: ProductScnLinkFilter;
  isActive: boolean | null;
}

export interface ProductPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ProductState {
  // Data (list echo — kept in sync by useProductsTable / mutation hooks)
  products: Product[];

  // Pagination echo
  pagination: ProductPagination;

  // Filters
  filters: ProductFilters;

  // Actions - data
  setProducts: (products: Product[]) => void;
  updateProductInList: (productId: string, updates: Partial<Product>) => void;

  // Actions - pagination
  setPagination: (pagination: Partial<ProductPagination>) => void;

  // Actions - filters
  setSearch: (search: string) => void;
  setCategoryPaths: (categoryPaths: string[][]) => void;
  setReadiness: (readiness: ProductReadinessFilter) => void;
  setScnLink: (scnLink: ProductScnLinkFilter) => void;
  setIsActive: (isActive: boolean | null) => void;
  resetFilters: () => void;

  // Actions - reset
  reset: () => void;
}

const initialState = {
  products: [] as Product[],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    categoryPaths: [] as string[][],
    readiness: 'all' as ProductReadinessFilter,
    scnLink: 'all' as ProductScnLinkFilter,
    isActive: true as boolean | null,
  },
};

export const useProductStore = create<ProductState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setProducts: (products) => set({ products }, false, 'products/setProducts'),

        updateProductInList: (productId, updates) =>
          set(
            (state) => ({
              products: state.products.map((p) => (p.id === productId ? { ...p, ...updates } : p)),
            }),
            false,
            'products/updateProductInList'
          ),

        setPagination: (pagination) =>
          set(
            (state) => ({ pagination: { ...state.pagination, ...pagination } }),
            false,
            'products/setPagination'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'products/setSearch'
          ),

        setCategoryPaths: (categoryPaths) =>
          set(
            (state) => ({
              filters: { ...state.filters, categoryPaths },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'products/setCategoryPaths'
          ),

        setReadiness: (readiness) =>
          set(
            (state) => ({
              filters: { ...state.filters, readiness },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'products/setReadiness'
          ),

        setScnLink: (scnLink) =>
          set(
            (state) => ({
              filters: { ...state.filters, scnLink },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'products/setScnLink'
          ),

        setIsActive: (isActive) =>
          set(
            (state) => ({
              filters: { ...state.filters, isActive },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'products/setIsActive'
          ),

        resetFilters: () =>
          set(
            {
              filters: { ...initialState.filters },
              pagination: { ...initialState.pagination },
            },
            false,
            'products/resetFilters'
          ),

        reset: () => set(initialState, false, 'products/reset'),
      }),
      {
        name: 'product-storage',
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
        }),
        // Ensure isActive falls back to the Active default (house list rule)
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<ProductState> | undefined;
          return {
            ...currentState,
            ...persisted,
            filters: {
              ...currentState.filters,
              ...persisted?.filters,
              isActive: persisted?.filters?.isActive ?? true,
            },
          };
        },
      }
    ),
    { name: 'ProductStore' }
  )
);
